import {
    IRectangle,
    ISize,
    IPoint,
    getBoundarySize,
    getCornerPointsFromCenter,
    rotateFromCenter,
    offsetPoints,
    getRectangleContainingPoints,
} from '../../geometry';

export enum LayerType {
    rect = 'rect',
    image = 'image',
    text = 'text',
    video = 'video',
    ellipse = 'ellipse',
    map = 'map',
}

export type BlendMode =
    | 'color'
    | 'difference'
    | 'hard-light'
    | 'hue'
    | 'multiply'
    | 'overlay'
    | 'saturation'
    | 'screen'
    | 'soft-light'
    | null
    | undefined;

export class Layer {
    public title = 'New Layer';

    public rotation = 0;

    public scale = 1.0;

    public skewX = 0;

    public skewY = 0;

    public position = {
        x: 0,
        y: 0,
    };

    public width = 150;

    public height = 150;

    public fill = 'rgb(0, 0, 0)';

    public stroke = 'rgb(0, 0, 0)';

    public strokeWidth = 0;

    public opacity = 1.0;

    public effects: [] = [];

    public selected = false;

    public visible = true;

    public blendMode: BlendMode | null = null;

    public lockAspectRatio = false;

    public constructor(
        public id: number,
        public type = LayerType.rect,
    ) {}
}

export class LayerGroup {
    private _layers: Layer[];

    public get selectedLayer(): Layer | null {
        if (this._layers.length === 1) {
            return this._layers[0];
        }

        return null;
    }

    public get layers(): Layer[] {
        return this._layers;
    }

    public get visibleLayers(): Layer[] {
        return this._layers.filter(x => x.visible);
    }

    public constructor(...layers: Layer[]) {

        this._layers = layers.filter(x => !!x);
    }

    public visibleLayersExists(): boolean {
        return this.visibleLayers.length > 0;
    }

    public add(layer: Layer): boolean {
        if (!this.contains(layer)) {
            this._layers.push(layer);
            return true;
        }

        return false;
    }

    public addOrRemove(layer: Layer): void {
        if (!this.add(layer)) {
            this.remove(layer);
        }
    }

    public remove(layer: Layer): void {
        this._layers = this._layers.filter(x => x.id !== layer.id);
    }

    public removeAll(): void {
        this._layers = [];
    }

    public isMultiple(visible?: boolean): boolean {

        if (visible === null || visible === undefined) {
            return this._layers.length > 1;
        }

        if (visible) {
            return this.visibleLayers.length > 1;
        } else {
            return this._layers.length - this.visibleLayers.length > 1;
        }
    }

    public contains(layer: Layer): boolean {
        return this._layers.some(x => x.id === layer.id);
    }

    public isAny(predicate?: (layer: Layer) => boolean): boolean {
        const isAny = !!this._layers.length;
        if (isAny && predicate) {
            return this.layers.some(predicate);
        }

        return isAny;
    }

    public isAnyVideo(): boolean {
        return this.isAny(layer => layer.type === LayerType.video);
    }

    public getContainingRectangle(includeRotation = true): IRectangle | null {
        if (this.isAny() && !this.isMultiple() && includeRotation) {
            return getBoundaryRectForLayer(this.selectedLayer!);
        }

        const layerCorners = this._layers
            .filter(x => x.visible)
            .map(layer => getLayerCornerPoints(layer))
            // eslint-disable-next-line id-length
            .reduce((a, b) => a.concat(b), []);
        return getRectangleContainingPoints(...layerCorners);
    }
}

export function getBoundaryForLayer(layer: Layer, zoom = 1): ISize {
    return getBoundarySize(
        { height: layer.height, width: layer.width },
        { skewX: layer.skewY, skewY: layer.skewX, zoom },
    );
}

export function getLayerCornerPoints(layer: Layer, zoom = 1): IPoint[] {
    const rect = getBoundaryForLayer(layer, zoom);
    const corners = getCornerPointsFromCenter(rect);
    const translatedCorners = offsetPoints(layer.position, ...corners);
    const rotatedCorners = translatedCorners.map(x => rotateFromCenter(x, layer.position, layer.rotation));
    return rotatedCorners;
}

export function getBoundaryRectForLayer(layer: Layer, zoom = 1): IRectangle {
    const size = getBoundaryForLayer(layer, zoom);
    return {
        height: size.height,
        width: size.width,
        x: layer.position.x,
        y: layer.position.y,
    };
}

export function getRectangleContainingForLayerCorners(layer: Layer, zoom = 1): IRectangle | null {
    return getRectangleContainingPoints(...getLayerCornerPoints(layer, zoom));
}
